import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { store } from './stores/store'
import {
  CUSTOMER_TAB_NAME,
  claimTabName,
  configureCognito,
  initWebCommon,
} from '@penn/web-common'
import { APP_CONTEXT } from './config/app-context'
import { clearAllProductSelections } from './utils/configure-storage'
import './assets/styles/globals.css'
import App from './App'

// Must precede anything that reads shared config. Only genuinely per-surface
// values live here; everything else the package reads from runtimeEnv itself.
claimTabName(CUSTOMER_TAB_NAME)

initWebCommon({
  appContext: APP_CONTEXT,
  requiredGroup: 'customer',
  oppositeGroup: 'admin',
  wrongSurfaceMessage:
    'This account is an internal staff account. Please sign in via the admin portal.',
  // No in-app sign-up under oidc: the Amplify form would register against this
  // app's own pool, which the session exchange no longer accepts.
  registerFallback: 'none',
})

configureCognito()

// Saved Configure selections never survive a page load: the machine options a
// product offers are resolved from the API per application/catalog state, so a
// selection carried over from a previous session can reference options that are
// no longer recommended.
clearAllProductSelections()

// BUG-LOW-aborted-xhr: benign cancelled/aborted XHRs — React Query cancelling an
// in-flight query on unmount, or a request the browser aborts on navigation —
// reject with an axios CanceledError / DOMException AbortError. Chromium ignores
// these; Firefox and WebKit surface them as uncaught "page errors" ("Request
// aborted" / "access control checks"). Swallow ONLY this cancellation class so
// genuine failures still propagate to the console and error boundaries.
const isBenignCancellation = (reason: unknown): boolean => {
  if (reason == null || typeof reason !== 'object') return false
  const r = reason as { name?: string; code?: string; message?: string }
  const message = String(r.message ?? '')
  return (
    r.name === 'CanceledError' || // axios v1 cancel
    r.name === 'AbortError' || // fetch / DOM abort
    r.code === 'ERR_CANCELED' || // axios cancel code
    /request aborted|the operation was aborted|the user aborted a request/i.test(
      message,
    )
  )
}
window.addEventListener('unhandledrejection', (event) => {
  if (isBenignCancellation(event.reason)) event.preventDefault()
})

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
})

createRoot(root).render(
  <StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Provider>
  </StrictMode>,
)
